import createApiClient from '@/api/axiosInstance';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PartModal from '../modals/PartModal';

const PART_FORM_STATE_ENUM = {
  CREATING_PART: 'CREATING_PART',
  DISPLAY_PART: 'DISPLAY_PART',
  EDIT_FORM: 'EDIT_FORM',
};

const q = 'Quotes Received';

const quote_status = (val) => {
  return (
    <div
      className={`inline-flex gap-2 p-1 ${
        val === 'RFQ Sent' && 'bg-[#fffad3] w-32'
      } ${val === 'Quotes Received' && 'bg-[#ebf9fc] w-40'} ${
        val === 'Quotation Agree' && 'bg-[#ebf5f9] w-40'
      } ${val === 'Quote Accepted' && 'bg-[#ebfcee] w-40'}
      } ${val === 'Team Approved' && 'bg-[#ebfcee] w-40'}
      } ${val === 'Contract Signed' && 'bg-[#faebf6] w-40'}
      } ${val === 'Awaiting Team Review' && 'bg-[#e6f4f9] w-48'}
      } ${val === 'Quotes' && 'bg-[#faebf6] w-40'}
       justify-center rounded-full items-center`}
    >
      <div
        className={`w-2 h-2 rounded-full ${
          val === 'RFQ Sent' && 'bg-[#af9800]'
        } ${val === 'Quotes Received' && 'bg-[#00859d]'} ${
          val === 'Quotation Agree' && 'bg-[#319bbb]'
        } ${val === 'Quote Accepted' && 'bg-[#33d84c]'} ${
          val === 'Team Approved' && 'bg-[#33d84c]'
        } ${val === 'Contract Signed' && 'bg-[#c42e9c]'} 
        } ${val === 'Awaiting Team Review' && 'bg-[#008dc1]'} 
        
        `}
      ></div>
      <div
        className={`${
          val === 'RFQ Sent' && 'text-[#af9800]'
        } ${val === 'Quotes Received' && 'text-[#00859d]'} ${
          val === 'Quotation Agree' && 'text-[#319bbb]'
        } ${val === 'Quote Accepted' && 'text-[#33d84c]'} ${
          val === 'Team Approved' && 'text-[#33d84c]'
        } ${val === 'Contract Signed' && 'text-[#c42e9c]'} 
        } ${val === 'Awaiting Team Review' && 'text-[#008dc1]'} 
        
        `}
      >
        {val}
      </div>
    </div>
  );
};

const buttons = (val) => {
  return (
    <>
      {val === 'RFQ Sent' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Track Team Status
        </Button>
      )}
      {val === 'Quotes Received' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          View Quotes
        </Button>
      )}
      {val === 'Quotes' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          View Quotes
        </Button>
      )}
      {val === 'Quotation Agree' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Create Contract
        </Button>
      )}
      {val === 'Quote Accepted' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Track Team Status
        </Button>
      )}
      {val === 'Awaiting Team Review' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Track Team Status
        </Button>
      )}
      {val === 'Team Approved' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Create Contract
        </Button>
      )}
      {val === 'Contract Signed' && (
        <Button className="bg-white border border-darkPurple text-darkPurple hover:bg-white">
          Create PO
        </Button>
      )}
    </>
  );
};

const AllPartsQuotationsTable = ({
  setScreen,
  setPart,
  tableData,
  checkedParts,
  setCheckedParts,
  searchTerm,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const { projectId, projectName } = useParams();
  const partsData = useSelector((state) => state?.projectReducer?.parts);

  const mergedTableData = useMemo(() => {
    return [
      ...tableData,
      ...partsData
        .filter(
          (part) => !tableData.some((item) => item.part_id === part.part_id),
        )
        .map((part) => ({
          part_id: part.part_id,
          part_name: part.part_name,
          part_number: part.part_number,
          no_of_manufacturer: 0,
          quotation_id: null,
          quote_number: null,
          received_date: null,
          manufacturer_id: null,
          manufacturer_name: null,
          quantity: null,
          total_price: null,
          estimated_delivery_date: null,
          status: 'No RFQ Sent',
        })),
    ].sort((a, b) => a.part_name.localeCompare(b.part_name));
  }, [tableData, partsData]);

  const filteredData = mergedTableData.filter((item) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (item?.part_name ?? '').toLowerCase().includes(lowerSearch);
  });

  // PARTS
  const [parts, setParts] = useState(partsData);
  const [showPartModal, setShowPartModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partFormState, setPartFormState] = useState(
    PART_FORM_STATE_ENUM.CREATING_PART,
  );

  const handleAddPart = (partData) => {
    if (selectedPart !== null) {
      const updatedParts = [...parts];
      updatedParts[selectedPart.index] = partData;
      setParts(updatedParts);
    } else {
      setParts((prev) => [...prev, partData]);
    }

    // Reset modal state
    setShowPartModal(false);
    setSelectedPart(null);
  };

  const handleViewPart = (index, part) => {
    setSelectedPart({ index: index, part: part });
    setPartFormState(PART_FORM_STATE_ENUM.DISPLAY_PART);
    setShowPartModal(true);
  };

  const handleDeletePart = (index) => {
    const updatedParts = [...parts];
    updatedParts.splice(index, 1);
    setParts(updatedParts);
  };

  // PART CHECK BOX
  const isAllChecked =
    filteredData.length > 0 && checkedParts.length === filteredData.length;

  const handleToggleAll = () => {
    if (isAllChecked) {
      setCheckedParts([]);
    } else {
      const newCheckedParts = tableData
        .map((row) => parts.find((p) => p.part_id === row.part_id))
        .filter(Boolean);
      setCheckedParts(newCheckedParts);
    }
  };

  const handleToggleRow = (part) => {
    const matchedPart = parts.find((p) => p.part_id === part.part_id);
    if (!matchedPart) return;

    const isAlreadyChecked = checkedParts.some(
      (p) => p.part_id === matchedPart.part_id,
    );
    if (isAlreadyChecked) {
      setCheckedParts(
        checkedParts.filter((p) => p.part_id !== matchedPart.part_id),
      );
    } else {
      setCheckedParts([...checkedParts, matchedPart]);
    }
  };

  const isChecked = (part) =>
    checkedParts.some((p) => p.part_id === part.part_id);

  const box = (checked, onChange) => {
    return (
      <Input
        type="checkbox"
        className="w-6 h-6 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
        checked={checked}
        onChange={onChange}
      ></Input>
    );
  };

  const handleViewQuote = (partId, partName) => {
    setPart({ partId: partId, partName: partName });
    setScreen('PART_QUOTE_DASHBOARD');
  };

  return (
    <div className="mt-2 font-roboto">
      <div className="grid grid-cols-custom6 items-center text-gray-600 text-center gap-4 py-2 bg-gray-50 rounded-md text-[14px]">
        <div className="flex justify-center">
          <Input
            type="checkbox"
            className="w-6 h-6 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
            checked={isAllChecked}
            onChange={handleToggleAll}
          />
        </div>
        <div className="text-left">Part</div>
        <div>No. of manufacturers</div>
        <div>Quote Recieved</div>
        <div>Finalized manufacturer</div>
        <div className="text-center">Action</div>
      </div>
      {filteredData.map((item, index) => {
        const uniqueKey = `default-${index}`;
        return (
          <div
            key={uniqueKey}
            className="grid grid-cols-custom6 gap-4  py-4 pl-0 items-center text-gray-500 text-sm border-b border-gray-300 text-center bg-white mt-2"
          >
            <div className="flex justify-center">
              {box(isChecked(item), () => handleToggleRow(item))}
            </div>
            <div className="text-left">
              <div>{item.part_name}</div>
              <div>{item.part_id}</div>
            </div>
            <div>{item.no_of_manufacturer}</div>
            <div>{item.quote_recieved}</div>
            <div>{item.manufacturer_name}</div>
            <Button
              className="bg-white border border-darkPurple text-darkPurple hover:bg-white"
              onClick={() => handleViewQuote(item.part_id, item.part_name)}
            >
              View Quotes
            </Button>
          </div>
        );
      })}
      {filteredData.length === 0 && (
        <div className="text-center text-gray-500 mt-6 text-sm">
          No matching parts found.
        </div>
      )}
      {/* <button
        className={`mt-8 text-base text-darkPurple font-medium transition ease-in-out duration-300 hover:bg-slate-100 flex items-center gap-2 px-2 py-1 border border-darkPurple rounded`}
        onClick={(e) => {
          e.preventDefault();
          setShowPartModal(true);
        }}
      >
        <Plus className="h-6 w-6" />
        Add Parts
      </button> */}

      <PartModal
        selectedPart={selectedPart}
        open={showPartModal}
        state={partFormState}
        onClose={() => {
          setShowPartModal(false);
          setSelectedPart(null);
        }}
        onSubmit={(data) => handleAddPart(data)}
      />
    </div>
  );
};

export default AllPartsQuotationsTable;
